import { useState } from "react";
import { useField } from "formik";
import style from "./FileInput.module.css";
// @ts-expect-error pure js
import useAuthenticatedAxios from "../../hooks/use-authenticated-axios";
import Callout from "./Callout";
// @ts-expect-error pure js
import { classnames, handleAPIError } from "../../utils";
import DocumentDownloadLink from "./DocumentDownloadLink";
// @ts-expect-error pure js
import DocumentThumbnail from "./DocumentThumbnail";
// @ts-expect-error pure js
import { DocType } from "../../utils/constants/documents";
// @ts-expect-error pure js
import { preprocessImage, validateUploadedFile } from "../../utils/documents";
import FileDropInput from "./FileDropInput";
import { useUploadLock } from "./useUploadLock";
import UploadProgress from "./UploadProgress";

export interface UploadedFile {
  src: string;
  filename?: string;
  mimetype?: string;
  localFile?: File;
}

interface FileInputProps {
  doctype?: string;
  label?: string;
  multiple?: boolean;
  path?: string;
  documentId?: number;
  text?: string;
  className?: string;
  accept?: string;
  format?: string;
  shouldWatermark?: boolean;
  disabled?: boolean;
}

export default function FileInput({
  doctype = DocType.GENERIC_FILE,
  label,
  multiple = false,
  path = "files",
  documentId,
  text = "Déposez vos fichiers",
  className,
  accept = ".png,.jpg,.jpeg,.pdf",
  format = "Format PNG, JPG, JPEG, PDF",
  shouldWatermark = false,
  disabled = false,
}: FileInputProps) {
  const axios = useAuthenticatedAxios();
  const [{ value }, meta, { setValue }] = useField<UploadedFile[]>(path);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(0);
  const [uploading, setUploading] = useState(false);
  const { acquire } = useUploadLock();

  async function handleFiles(selectedFiles: File[]) {
    const formData = new FormData();

    // Le formulaire reste verrouillé jusqu'à la réponse du serveur : c'est elle
    // qui rattache les fichiers au formulaire.
    const release = acquire();
    setUploading(true);
    setLoading(0);
    try {
      for (const file of selectedFiles) {
        const validationError = validateUploadedFile(file);
        if (validationError) {
          setError(validationError);
          return;
        }

        if (file.type.startsWith("image/")) {
          const resizedFile = await preprocessImage(file, shouldWatermark);
          formData.append("documents", resizedFile);
        } else {
          formData.append("documents", file);
        }

        if (!multiple) break;
      }

      const res = await axios.post("/documents/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        params: {
          doctype,
        },
        onUploadProgress: function (progressEvent: {
          loaded: number;
          total?: number;
        }) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1),
          );
          setLoading(percentCompleted);
        },
      });
      const newFiles = res.data.map(
        (file: UploadedFile, i: number): UploadedFile => {
          return {
            ...file,
            localFile: selectedFiles[i],
          };
        },
      );
      if (multiple) {
        await setValue([...value, ...newFiles]);
      } else {
        await setValue(newFiles);
      }
      setError("");
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setUploading(false);
      release();
    }
  }

  async function deleteFileHandler(fileToDelete: UploadedFile) {
    await setValue(value.filter((file) => file.src !== fileToDelete.src));
  }

  return (
    <div className={classnames(className)}>
      {label && <p>{label}</p>}
      <>
        {!disabled && (
          <FileDropInput
            onFiles={handleFiles}
            accept={accept}
            multiple={multiple}
            text={text}
            format={format}
            hasError={Boolean(meta.error && meta.touched)}
          />
        )}
        {meta.touched && meta.error && (
          <div className={style.error}>{meta.error}</div>
        )}
        <UploadProgress percent={loading} uploading={uploading} />
        <Callout variant="danger" className={style.callout}>
          {error}
        </Callout>
      </>
      <div className={style.thumbnails}>
        {value.map((file, i) => (
          <div key={file.src || `pending-${i}`}>
            <DocumentDownloadLink
              documentId={documentId}
              doctype={doctype}
              file={file}
            >
              <DocumentThumbnail
                documentId={documentId}
                doctype={doctype}
                file={file}
                onDelete={!disabled && deleteFileHandler}
              />
            </DocumentDownloadLink>
          </div>
        ))}
      </div>
    </div>
  );
}
